import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Save } from 'lucide-react';
import { Section, Field, Modal, ImageUpload } from './_shared';

const empty = { category: 'm3', name: '', arabic: '', sector: '', status: '', blurb: '', url: '', logo_base64: '', order: 0 };

export default function VenturesPanel({ cx }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('m3');

  const load = async () => { setLoading(true); try { const r = await cx.get('/ventures'); setItems(r.data || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...editing }; delete payload.id; delete payload.created_at;
      if (editing.id) await cx.put(`/ventures/${editing.id}`, payload);
      else await cx.post('/ventures', payload);
      toast.success('Saved'); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this venture?')) return;
    try { await cx.delete(`/ventures/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const filtered = items.filter((v) => v.category === filter);

  return (
    <Section
      title="Ventures"
      action={
        <button data-testid="admin-venture-add" onClick={() => setEditing({ ...empty, category: filter })} className="btn-primary text-sm">
          <Plus size={14} /> Add venture
        </button>
      }
    >
      <div className="inline-flex p-1 rounded-full border border-subtle surface mb-5">
        {[
          { k: 'm3', label: 'M³ Ventures' },
          { k: 'subsidiary', label: 'Subsidiaries' },
          { k: 'portfolio', label: 'Portfolio' },
        ].map((f) => (
          <button key={f.k} data-testid={`admin-venture-filter-${f.k}`} onClick={() => setFilter(f.k)} className={`px-4 py-1.5 rounded-full text-xs font-medium ${filter === f.k ? 'bg-brand-maroon text-white' : 'text-sec hover:text-prim'}`}>{f.label}</button>
        ))}
      </div>

      {loading ? <p className="text-sec text-sm font-mono">Loading…</p> : filtered.length === 0 ? <p className="text-muted text-sm">No ventures in this category yet.</p> : (
        <div className="space-y-2">
          {filtered.map((v) => (
            <div key={v.id} data-testid={`admin-venture-row-${v.id}`} className="border border-subtle surface rounded-md p-4 flex gap-4 items-start">
              <div className="w-14 h-14 rounded-md overflow-hidden surface-2 shrink-0 flex items-center justify-center">
                {v.logo_base64 ? <img src={v.logo_base64} className="photo-fit" alt="" /> : <span className="font-display text-sm text-muted">{v.name?.[0]}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display text-prim">{v.name}</p>
                  {v.status && <span className="text-[10px] font-mono uppercase tracking-wider text-brand-teal-light">{v.status}</span>}
                </div>
                {v.sector && <p className="text-xs text-muted font-mono mt-1">{v.sector}</p>}
                {v.blurb && <p className="text-sec text-sm mt-1 line-clamp-2">{v.blurb}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(v)} className="text-xs text-brand-teal-light hover:underline flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button onClick={() => del(v.id)} className="text-xs text-brand-maroon-light hover:underline flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? 'Edit venture' : 'New venture'}>
          <div className="space-y-4">
            <Field label="Category">
              <select className="lux-input" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                <option value="m3">M³ Ventures</option>
                <option value="subsidiary">Subsidiary</option>
                <option value="portfolio">Portfolio company</option>
              </select>
            </Field>
            <ImageUpload value={editing.logo_base64} onChange={(v) => setEditing({ ...editing, logo_base64: v })} label="Upload logo" />
            <Field label="Name"><input className="lux-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="Sector / tagline"><input className="lux-input" value={editing.sector} onChange={(e) => setEditing({ ...editing, sector: e.target.value })} /></Field>
            <Field label="Arabic accent (optional)"><input className="lux-input font-arabic" value={editing.arabic} onChange={(e) => setEditing({ ...editing, arabic: e.target.value })} /></Field>
            <Field label="Status (Operations / Concept / blank)">
              <select className="lux-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="">—</option>
                <option value="Operations">Operations</option>
                <option value="Concept">Concept</option>
              </select>
            </Field>
            <Field label="Short blurb"><textarea rows={3} className="lux-input resize-none" value={editing.blurb} onChange={(e) => setEditing({ ...editing, blurb: e.target.value })} /></Field>
            <Field label="Website URL"><input className="lux-input" placeholder="https://…" value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} /></Field>
            <Field label="Order"><input type="number" className="lux-input" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || 0) })} /></Field>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={save} data-testid="admin-venture-save" className="btn-primary text-sm"><Save size={14} /> Save</button>
            </div>
          </div>
        </Modal>
      )}
    </Section>
  );
}
